import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { resolveImg } from "@/lib/util";
import { DynIcon } from "@/components/site/Icon";
import { PublicLayout } from "@/components/site/PublicLayout";

export default function PracticeAreaDetail() {
  const { id } = useParams();
  const [area, setArea] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/practice-areas/${id}`).then((r) => setArea(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useSeo({
    title: area ? `${area.title} | Adv. Guruvayoorappan K V, Thrissur` : "Practice Area",
    description: area?.short_desc,
  });

  if (notFound) return <PublicLayout><div className="max-w-3xl mx-auto px-6 py-24 text-center"><h1 className="font-serif text-3xl text-navy mb-4">Practice area not found</h1><Link to="/practice-areas" className="text-brass">Back to Practice Areas</Link></div></PublicLayout>;
  if (!area) return <PublicLayout><div className="max-w-3xl mx-auto px-6 py-32" /></PublicLayout>;

  return (
    <PublicLayout>
      <section className="bg-navy py-14 lg:py-20" data-testid="practice-detail-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Link to="/practice-areas" className="inline-flex items-center gap-2 text-ivory/50 hover:text-brass text-sm mb-6"><ArrowLeft size={15} /> All Practice Areas</Link>
          <div className="flex items-start gap-5">
            <span className="w-14 h-14 rounded-sm bg-brass text-navy flex items-center justify-center shrink-0"><DynIcon name={area.icon} size={26} /></span>
            <div>
              <h1 className="font-serif text-3xl lg:text-5xl text-ivory leading-tight">{area.title}</h1>
              <p className="text-ivory/60 mt-3 max-w-2xl leading-relaxed">{area.short_desc}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          {area.image_url && (
            <div className="rounded-lg overflow-hidden mb-8 border border-slate-200" style={{ aspectRatio: "3/2" }}>
              <img src={resolveImg(area.image_url)} alt={area.title} className="w-full h-full object-cover" />
            </div>
          )}
          <p className="text-slate-600 text-lg leading-relaxed mb-8 whitespace-pre-line">{area.long_desc}</p>
          {area.services?.length > 0 && (
            <>
              <h2 className="font-serif text-2xl text-navy mb-4">Services Include</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {area.services.map((s, i) => (
                  <li key={i} className="flex items-start gap-3 bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-navy">
                    <Check size={16} className="text-brass shrink-0 mt-0.5" /> {s}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <aside className="lg:col-span-1">
          <div className="bg-navy rounded-lg p-7 sticky top-28" data-testid="practice-detail-cta">
            <h3 className="font-serif text-xl text-ivory mb-3">Discuss Your Legal Matter</h3>
            <p className="text-ivory/60 text-sm mb-6 leading-relaxed">Submit an enquiry describing your matter for a preliminary discussion, subject to professional availability.</p>
            <Link to="/contact" className="block bg-brass hover:bg-brass-hover text-navy font-semibold text-center px-5 py-3 rounded-sm transition-colors" data-testid="practice-detail-enquiry">
              Request a Consultation
            </Link>
          </div>
        </aside>
      </section>
    </PublicLayout>
  );
}
